import {H1Dashboard} from '@hm/fundamental'
import {buildFeatureUrl, useBreadcrumbLabels} from '@hproduce/suite-core'
import React from 'react'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router'

import {MenuNodeType} from '../../../api/types/featuresMenu'
import {NOT_FOUND_STATUS_CODE} from '../../../app/routing/routes'
import {useFeaturesMenu} from '../../../app/welcomeFlow/useFeaturesMenu'
import {usePathSegments} from '../../../hooks/usePathSegments'
import {findSameLevelMenuEntries} from '../../../utils/findSameLevelMenuEntries'

import {BreadcrumbSegment} from './BreadcrumbSegment'
import {BreadcrumbSiblingFeatureDropdown} from './BreadcrumbSiblingFeatureDropdown'
import {useHierarchyIdFromURL} from './PlantSelectButton/useHierarchyIdFromUrl'

const BreadcrumbSeparator = () => {
  return <H1Dashboard className="text-foreground_faded">{'|'}</H1Dashboard>
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const buildSegmentUrl = (
  segments: string[],
  index: number,
  hierarchyId: string | null
): string => {
  if (index === 0) {
    return buildFeatureUrl(segments[0], hierarchyId)
  }
  const subSegments = segments.slice(1, index + 1).join('/')
  return `${buildFeatureUrl(segments[0], hierarchyId)}/${subSegments}`
}

export const FeatureBreadcrumbs: React.FC = () => {
  const {t} = useTranslation()
  const navigate = useNavigate()

  const featureSegments = usePathSegments({excludeHierarchyId: true})
  const [hierarchyId] = useHierarchyIdFromURL()
  const {featuresMenu} = useFeaturesMenu()
  const breadcrumbLabels = useBreadcrumbLabels()

  const isApplicationRoot = featureSegments.length === 0
  const isPageNotFound =
    featureSegments.length === 1 && featureSegments[0] === NOT_FOUND_STATUS_CODE

  const featureName = featureSegments[0]
  const features = featuresMenu
    ? findSameLevelMenuEntries(featuresMenu, featureName, true, MenuNodeType.Feature)
    : []

  const isSiblingFeaturesExist = features.length > 1

  if (isApplicationRoot || isPageNotFound) {
    return (
      <div className="flex items-center gap-3" data-test-id="feature_breadcrumb">
        <BreadcrumbSegment isActive>{t('suite.featureBreadcrumb.root')}</BreadcrumbSegment>
      </div>
    )
  }

  const renderedSegments = featureSegments.flatMap((segment, index) => {
    const registeredLabel = breadcrumbLabels[segment]
    const isUnresolvedDynamicSegment = !registeredLabel && UUID_REGEX.test(segment)

    if (isUnresolvedDynamicSegment) {
      return []
    }

    return [
      {
        segment,
        index,
        label: registeredLabel ?? t(`suite.featureName.${segment}`, {defaultValue: segment})
      }
    ]
  })

  return (
    <div className="flex items-center gap-3" data-test-id="feature_breadcrumb">
      {renderedSegments.map(({index, label}, renderIndex) => {
        const breadcrumbKey = buildSegmentUrl(featureSegments, index, hierarchyId)

        return (
          <React.Fragment key={breadcrumbKey}>
            <span className="flex items-center">
              <BreadcrumbSegment
                isActive={renderIndex === renderedSegments.length - 1}
                onClick={
                  renderIndex < renderedSegments.length - 1
                    ? () => void navigate(buildSegmentUrl(featureSegments, index, hierarchyId))
                    : undefined
                }
              >
                {label}
              </BreadcrumbSegment>
              {isSiblingFeaturesExist && index === 0 && (
                <BreadcrumbSiblingFeatureDropdown features={features} />
              )}
            </span>
            {renderIndex < renderedSegments.length - 1 && <BreadcrumbSeparator />}
          </React.Fragment>
        )
      })}
    </div>
  )
}
